import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AuthState,
  getIconClass,
  LanguageState,
  NavigationState,
  PermissionsState,
  TypesUtilsService,
  UserPreferencesState,
} from '@osos/shared/legacy-core';
import {
  AccessRightLevelEnum,
  DATA_VIEW_QUERY_ID_PARAM,
  ItemMenu,
  LinkPresenterModel,
  PinnedEntities,
  ServiceViewsStatusModel,
} from '@osos/shared/types';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import {
  ADMIN_MENU_ITEMS,
  PUBLIC_SERVICES_MENU_ITEMS,
  PublicMenuItemsEnum,
} from './static-menu-items';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MenuComponent implements OnInit {
  menuItems$: Observable<ItemMenu[]>;
  adminMenuItems$: Observable<ItemMenu[]>;
  servicesMenuItems$: Observable<ItemMenu[]>;
  adminMenuItemsReady$: Observable<boolean>;
  hasActiveDelegation: boolean;
  rtlDir$: Observable<boolean> = this.store.select(
    LanguageState.currentLanguageRTLDirection,
  );

  trackByFn = (_, item: ItemMenu) => item.id;

  constructor(
    private store: Store,
    private typesUtils: TypesUtilsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.hasActiveDelegation = !!this.store.selectSnapshot(
      UserPreferencesState.activeDelegationId,
    );
    // Admin menu items:
    this.adminMenuItems$ = this.store
      .select(PermissionsState.getCurrentAdminLevel)
      .pipe(
        filter((level) => level !== undefined),
        map((adminLevel) =>
          adminLevel > AccessRightLevelEnum.None
            ? this._prepareAdminMenuItems(adminLevel)
            : [],
        ),
        shareReplay({ bufferSize: 1, refCount: true }), // Share replay to be used with combineLatest
      );
    // Used to emit the value only after adminMenuItems$ emits its value, if authenticated:
    this.adminMenuItemsReady$ = this.store
      .select(AuthState.isAuthenticated)
      .pipe(
        switchMap((isAuth) => (isAuth ? this.adminMenuItems$ : of([]))),
        map(() => true),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.servicesMenuItems$ = combineLatest([
      this.store.select(NavigationState.servicesNavigationConfig),
      this.store.select(NavigationState.sidebarStatus).pipe(filter((v) => !!v)),
      this.adminMenuItemsReady$,
    ]).pipe(
      filter(([servicesNavConfig]) => !!servicesNavConfig),
      map(([servicesNavConfig, sidebarStatus]) => {
        return servicesNavConfig.enabled
          ? this.modifyServiceItemsWithCount(
              PUBLIC_SERVICES_MENU_ITEMS,
              sidebarStatus,
            )
          : [];
      }),
      // tap((emitted) => console.debug('[MenuComponent] servicesMenuItems emitted', emitted.map(e => ({ id: e.id, count: e.count })))), // Uncomment for debugging
      // tap((items) => console.debug('[Menu] servicesMenuItems$ emitted', items.map(i => ({id: i.id, count: i.count})))), // DEBUG
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.menuItems$ = this.store.select(NavigationState.navigationItemsV2).pipe(
      filter((nav) => !!nav),
      map((nav) => this._prepareViewsByModuleItemsV2(nav.links)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  _prepareAdminMenuItems(
    adminLevel: AccessRightLevelEnum,
    items: any[] = ADMIN_MENU_ITEMS,
  ): ItemMenu[] {
    return items
      .filter((item) => item.permissionLevel <= adminLevel)
      .map((item) => ({
        ...item,
        preloadItems: true,
        items: !item.items?.length
          ? undefined
          : this._prepareAdminMenuItems(adminLevel, item.items),
      }));
  }

  onMenuCalculated(): void {
    this.cdr.detectChanges();
  }

  // private _prepareGlobalGenericViewsItems(
  //   genericViews: NavigationItem[]
  // ): ItemMenu[] {
  //   return genericViews
  //     ?.map<ItemMenu>((view) => ({
  //       id: view.id,
  //       localeLabel: view?.name,
  //       icon: `${
  //         getIconClass(view?.icon?.name) ?? 'pi pi-circle-off visible-0'
  //       }`,
  //       routerLink: `/${view.id}`,
  //       iconClass: 'color-secondary',
  //     }))
  //     .sort(this._sortByLabel.bind(this));
  // }

  // private _prepareViewsByModuleItems(
  //   modules: DataModuleWithViewItems[]
  // ): ItemMenu[] {
  //   if (modules.length === 1) {
  //     return this._prepareViewsItems(modules[0].views);
  //   } else {
  //     return modules
  //       ?.map<ItemMenu>((mdl) => {
  //         const items = this._prepareViewsItems(mdl?.views).sort((a, b) =>
  //           a.label < b.label ? -1 : 1
  //         );
  //         if (items.length === 1) {
  //           if (
  //             this.typesUtils.getLocaleString(mdl.name) ===
  //             this.typesUtils.getLocaleString(items[0].localeLabel)
  //           ) {
  //             return items[0];
  //           }
  //         }
  //         return {
  //           id: mdl.id,
  //           localeLabel: mdl?.name,
  //           icon: `${
  //             getIconClass(mdl?.icon?.name) ?? 'pi pi-circle-off visible-0'
  //           }`,
  //           iconClass: 'color-primary',
  //           routerLink: `/${mdl.id}`,
  //           items: items,
  //         };
  //       })
  //       .sort(this._sortByLabel.bind(this));
  //   }
  // }

  private _prepareViewsByModuleItemsV2(
    modules: LinkPresenterModel[],
  ): ItemMenu[] {
    return modules?.map<ItemMenu>((mdl) => {
      const items = this._prepareViewsItemsV2(mdl?.children);

      return {
        id: mdl.id,
        localeLabel: mdl?.title,
        icon: `${
          getIconClass(mdl?.icon?.name) ?? 'pi pi-circle-off visible-0'
        }`,
        iconClass: 'color-primary',
        routerLink:
          mdl.type.id === PinnedEntities.Module ? undefined : `${mdl.url}`,
        type: mdl.type.id === PinnedEntities.UnTyped ? 'external' : 'internal',
        items: items,
        description: mdl.description,
      };
    });
  }
  // private _prepareViewsItems(views: LinkPresenterModel[] = []): ItemMenu[] {
  //   return views?.map((view) => {
  //     const _view: ItemMenu = {
  //       id: view.id,
  //       localeLabel: view?.title,
  //       icon: `${
  //         getIconClass(view?.icon?.name) ?? 'pi pi-circle-off visible-0'
  //       }`,
  //       routerLink: view.url,
  //     };

  //     switch (view.type) {
  //       case PinnedEntities.GenericView: {
  //         _view.iconClass = 'color-secondary';
  //         break;
  //       }
  //       case PinnedEntities.DataViews: {
  //         _view.routerLinkActiveOptions = {
  //           exact: false,
  //           paths: 'subset',
  //           queryParams: 'subset',
  //           fragment: 'ignored',
  //           matrixParams: 'ignored',
  //         };
  //         _view.iconClass = 'color-accent';
  //         _view.hasRelatedItems = view.hasRelatedItems;

  //         break;
  //       }
  //       case PinnedEntities.Reports: {
  //         break;
  //       }
  //       case PinnedEntities.UserCollections: {
  //         _view.routerLinkActiveOptions = { exact: true };
  //         _view.iconBadge = { icon: 'icon-umbrella', color: '#2caa90' };
  //         _view.isRelatedItem = true;
  //         _view.isLastRelatedItem = view.isLastRelatedItem;
  //         break;
  //       }
  //       case PinnedEntities.UserQueries: {
  //         _view.routerLinkActiveOptions = { exact: true };
  //         _view.queryParams = {
  //           [DATA_VIEW_QUERY_ID_PARAM]: view.id,
  //         };
  //         _view.iconBadge = {
  //           icon: 'icon-electric',
  //           color: '#147aff',
  //         };
  //         _view.isRelatedItem = true;
  //         _view.isLastRelatedItem = view.isLastRelatedItem;
  //         break;
  //       }
  //     }
  //     return _view;
  //   });
  // }

  private _prepareViewsItemsV2(views: LinkPresenterModel[] = []): ItemMenu[] {
    return views?.map((view) => {
      const _view: ItemMenu = {
        id: view.id,
        localeLabel: view?.title,
        icon: `${
          getIconClass(view?.icon?.name) ?? 'pi pi-circle-off visible-0'
        }`,
        routerLink: view.url,
        type: 'internal',
        description: view.description,
      };

      switch (view.type.id) {
        case PinnedEntities.GenericView: {
          _view.iconClass = 'color-secondary';
          break;
        }
        case PinnedEntities.DataViews: {
          _view.routerLinkActiveOptions = {
            exact: false,
            paths: 'subset',
            queryParams: 'subset',
            fragment: 'ignored',
            matrixParams: 'ignored',
          };
          _view.iconClass = 'color-accent';
          _view.hasRelatedItems = view.hasRelatedItems;

          break;
        }
        case PinnedEntities.Reports: {
          _view.routerLink = view.url;
          break;
        }
        case PinnedEntities.UserCollections: {
          _view.routerLinkActiveOptions = { exact: true };
          _view.iconBadge = { icon: 'icon-umbrella', color: '#2caa90' };
          _view.isRelatedItem = true;
          _view.isLastRelatedItem = view.isLastRelatedItem;
          break;
        }
        case PinnedEntities.SharedCollections: {
          _view.routerLinkActiveOptions = { exact: true };
          _view.iconBadge = { icon: 'icon-umbrella', color: '#ff8a14' };
          _view.isRelatedItem = true;
          _view.isLastRelatedItem = view.isLastRelatedItem;
          break;
        }
        case PinnedEntities.UserQueries: {
          _view.routerLinkActiveOptions = { exact: true };
          _view.queryParams = {
            [DATA_VIEW_QUERY_ID_PARAM]: view.target?.targetId,
          };
          _view.iconBadge = {
            icon: 'icon-elec',
            color: '#147aff',
          };
          _view.isRelatedItem = true;
          _view.isLastRelatedItem = view.isLastRelatedItem;
          break;
        }
        case PinnedEntities.UnTyped: {
          _view.type = 'external';
          break;
        }
      }
      return _view;
    });
  }

  private modifyServiceItemsWithCount(
    items: ItemMenu[],
    sidebarServiceStatus: ServiceViewsStatusModel,
  ): ItemMenu[] {
    return items.reduce<ItemMenu[]>((acc, item) => {
      // Always clone to ensure new reference for OnPush change detection
      let newItem: ItemMenu = { ...item };
      if (item.id === PublicMenuItemsEnum.Inbox) {
        newItem = {
          ...newItem,
          count: sidebarServiceStatus.onGoingInbox,
        };
      }
      if (item.id === PublicMenuItemsEnum.Request) {
        newItem = {
          ...newItem,
          count: sidebarServiceStatus.onGoingRequests,
        };
      }
      if (item.id === PublicMenuItemsEnum.RequestMonitoring) {
        if (!sidebarServiceStatus.hasObserverRequests) {
          return acc; // Skip item entirely if not relevant
        }
        newItem = {
          ...newItem,
          count: sidebarServiceStatus.onGoingObserverRequests,
        };
      }
      newItem.items = newItem?.items?.length
        ? this.modifyServiceItemsWithCount(newItem.items, sidebarServiceStatus)
        : undefined;
      acc.push(newItem);
      return acc;
    }, []);
  }
}
